import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  BalancedFeedbackData,
  CoachingHistoryItem,
  NewActionDraft,
  SignatureData,
  FollowUpResult,
  WeeklyMetricRow,
  WeeklyMetricCell,
} from '../common/types/beginActivity.common.types';
import { ActivitySession } from '../PeopleActivityScreen';
import type { KfaImage } from './ObsImageUploadModal';
import { fetchChecklistItems, saveActivityDraft, completeActivity } from '../../peopleHubApi';
import { mapWeeklyMetricRows, mapPeriodMetricRows, mapCoachingHistoryItems } from '../../EmployeeProfile/mapEmployeeProfileResponse';
import { toSignaturePayload, mapResumedSignatures, fromCoachingResultEnum, withCreatedBy, buildNewActionPlanPayload, buildCoachingResultPayload, validateNewActionPlan } from '../common/activityPayload';

export type ObsCategory = 'before' | 'pit' | 'ergo' | 'process' | 'after';

/** A catalog checklist item as fetched from the backend (text + its itemId for payloads). */
export interface ObsFetchedItem {
  itemId: string;
  text: string;
}

interface CustomChecklistItemPayload {
  id: string;
  checklistItem: string;
}

interface ActivityImagePayload {
  checklistItemId: string;
  imageId: string;
  imageBase64: string;
  description: string;
}

const OBS_CATEGORIES: ObsCategory[] = ['before', 'pit', 'ergo', 'process', 'after'];

function isObsCategory(value: string): value is ObsCategory {
  return OBS_CATEGORIES.includes(value as ObsCategory);
}

function mapResumedCustomItemsToCategories(
  items: Array<{ id: string; checklistItem: string }> | undefined,
): Record<ObsCategory, string[]> {
  const initial: Record<ObsCategory, string[]> = {
    before: [],
    pit: [],
    ergo: [],
    process: [],
    after: [],
  };
  (items ?? []).forEach((item) => {
    const prefix = String(item.id ?? '').split('_')[0]?.toLowerCase();
    if (!prefix || !isObsCategory(prefix)) return;
    if (!item.checklistItem) return;
    initial[prefix].push(item.checklistItem);
  });
  return initial;
}

// Stage name sent in the `stage` field of POST .../save, keyed by step index (0-2)
const STEP_STAGE = ['BEFORE_ACTIVITY', 'DURING_ACTIVITY', 'AFTER_ACTIVITY'] as const;
// Which checklist categories belong to each stage
const STAGE_CATEGORIES: Record<(typeof STEP_STAGE)[number], ObsCategory[]> = {
  BEFORE_ACTIVITY: ['before'],
  DURING_ACTIVITY: ['pit', 'ergo', 'process'],
  AFTER_ACTIVITY: ['after'],
};

/** Resolves the step to resume on from the resumed activity's `stage` field. */
function stepFromStage(stage: string | undefined): ObsStep {
  const idx = STEP_STAGE.indexOf(stage as (typeof STEP_STAGE)[number]);
  return (idx >= 0 ? idx : 0) as ObsStep;
}

// Maps our internal checklist keys to the backend `category` query values
const CHECKLIST_CATEGORY: Record<ObsCategory, string> = {
  before:  'Before Activity',
  pit:     'During Activity - PIT',
  ergo:    'During Activity - Ergo',
  process: 'During Activity - Process',
  after:   'After Activity',
};

// ─── Step definitions (mirrors ka in HTML) ────────────────────────────────────
export const OBS_STEPS = [
  { title: 'Before Activity', sub: 'Preparation'         },
  { title: 'During Activity', sub: 'PIT, Ergo, and Process' },
  { title: 'After Activity',  sub: 'Completion'           },
  { title: 'Coaching',        sub: 'Feedback and action'  },
] as const;

export type ObsStep = 0 | 1 | 2 | 3;

// ─── During-Activity sub-bands (mirrors Aa in HTML) ───────────────────────────
export const DURING_BANDS = [
  { key: 'pit'     as const, label: 'PIT',     sub: 'Equipment behaviors'   },
  { key: 'ergo'    as const, label: 'ERGO',    sub: 'Ergonomic behaviors'   },
  { key: 'process' as const, label: 'PROCESS', sub: 'Operational standards' },
];

// ─── Mock metric rows (same structure as PulseCheck) ─────────────────────────
function makeCell(wk: string, actual: number, target: number): WeeklyMetricCell {
  return { weekLabel: wk, actual, target, state: actual >= target ? 'met' : 'miss' };
}

function buildMetricRows(): WeeklyMetricRow[] {
  const periods = ['P5W4','P6W1','P6W2','P6W3','P6W4','P7W1','P7W2','P7W3','P7W4','P8W1','P8W2','P8W3'];
  return [
    { metricName: 'DPS',         unit: '%',     rollingAverage: 0.979, weeksMet: 9,  weeksTotal: 12, cells: [0.921,0.953,0.976,0.948,0.987,0.992,0.963,0.971,1.002,1.014,0.986,1.031].map((v,i)=>makeCell(periods[i],v,1.000)) },
    { metricName: 'KU',          unit: '%',     rollingAverage: 0.931, weeksMet: 7,  weeksTotal: 12, cells: [0.910,0.918,0.922,0.914,0.931,0.936,0.923,0.928,0.939,0.941,0.952,0.956].map((v,i)=>makeCell(periods[i],v,0.950)) },
    { metricName: 'Gap Time',    unit: 'sec',   rollingAverage: 5.1,   weeksMet: 6,  weeksTotal: 12, cells: [5.8,5.5,5.3,5.4,5.1,4.9,5.2,4.8,4.7,4.9,4.8,4.6].map((v,i)=>makeCell(periods[i],v,5.0)) },
    { metricName: 'BPA',         unit: 'ratio', rollingAverage: 1.22,  weeksMet: 8,  weeksTotal: 12, cells: [1.12,1.15,1.18,1.14,1.22,1.25,1.20,1.23,1.31,1.29,1.24,1.32].map((v,i)=>makeCell(periods[i],v,1.25)) },
    { metricName: 'Cases Picked',unit: 'cases', rollingAverage: 1169,  weeksMet: 8,  weeksTotal: 12, cells: [1056,1089,1132,1098,1176,1205,1158,1192,1248,1276,1214,1188].map((v,i)=>makeCell(periods[i],v,1150)) },
  ];
}

// ─── Mock coaching data (same as PulseCheck) ─────────────────────────────────
function buildMockOpenItems(employeeId: string): CoachingHistoryItem[] {
  return [{
    id: `obs-open-${employeeId}-1`,
    employeeId,
    source: 'Observation',
    activityId: 'act-obs-001',
    date: '2026-06-09',
    submittedBy: 'S. Okafor',
    category: 'Metric',
    focus: 'Gap Time',
    plan: 'Stage the replenishment pallets before the wave to hold gap time under 5 minutes.',
    status: 'Pending',
    followUpResult: '',
    followUpFeedback: '',
    reviewHistory: [],
  }];
}

function buildMockHistoryItems(employeeId: string): CoachingHistoryItem[] {
  return [{
    id: `obs-hist-${employeeId}-1`,
    employeeId,
    source: 'Observation',
    activityId: 'act-obs-090',
    date: '2026-07-10',
    submittedBy: 'S. Okafor',
    category: 'Process',
    focus: 'Geo Loading',
    plan: 'Load to the geo sequence on every outbound trailer this period.',
    status: 'Completed',
    followUpResult: 'Complete',
    followUpFeedback: '',
    completedOn: '2026-07-30',
    reviewHistory: [],
  }];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useRoutineObservation(session: ActivitySession) {
  const userInfo = useSelector((state: any) => state.appData?.userInfo);
  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const fieldUsers = useSelector((state: any) => state.appData?.FieldUsers ?? []);
  const coeUsers = useSelector((state: any) => state.appData?.COEUsers ?? []);
  const employeesRaw = useSelector((state: any) => state.appData?.employees);
  const employeeRoster = useMemo(
    () => (Array.isArray(employeesRaw) ? employeesRaw : employeesRaw?.data ?? []),
    [employeesRaw],
  );
  const profileData = useSelector((state: any) => state.peopleHub?.employeeProfile?.data);
  const isRoutine = session.activityType === 'routine_observation';
  const role = session.role;

  // ── Metric rows (observation mode has no 12-Wk Avg / Weeks Met cols) ──
  const metricRows = useMemo<WeeklyMetricRow[]>(() => {
    const mapped = mapWeeklyMetricRows(profileData?.metrics12Week);
    return mapped.length > 0 ? mapped : buildMetricRows();
  }, [profileData]);

  // ── Period rows — shown when the Last Twelve Weeks card is toggled to "Period" ──
  const periodMetricRows = useMemo<WeeklyMetricRow[]>(() => {
    const mapped = mapPeriodMetricRows(profileData?.metrics12Period);
    return mapped.length > 0 ? mapped : metricRows;
  }, [profileData, metricRows]);

  // ── Step navigation ────────────────────────────────────────────────────
  // `step`    = currently displayed step (0-3)
  // `reached` = highest step ever visited (gates tab access)
  // Resuming an in-progress activity lands on the stage it was last saved on.
  const resumeStep = stepFromStage(session.stage);
  const [step, setStep]       = useState<ObsStep>(resumeStep);
  const [reached, setReached] = useState<ObsStep>(resumeStep);

  // Whenever step increases, advance `reached` if needed
  useEffect(() => {
    setReached((prev) => (step > prev ? step : prev) as ObsStep);
  }, [step]);

  const goNext = () => setStep((s) => Math.min(3, s + 1) as ObsStep);
  const goBack = () => setStep((s) => Math.max(0, s - 1) as ObsStep);
  const goToStep = (s: ObsStep) => {
    if (s <= reached) setStep(s);
  };

  // ── Per-KFA photo store ────────────────────────────────────────────────
  // Key = question text; value = up to 2 KfaImage objects
  const [itemPhotos, setItemPhotos] = useState<Record<string, KfaImage[]>>({});

  const setKfaPhotos = (questionText: string, images: KfaImage[]) => {
    setItemPhotos((prev) => ({ ...prev, [questionText]: images }));
  };

  const getKfaPhotos = (questionText: string): KfaImage[] =>
    itemPhotos[questionText] ?? [];

  // Count only non-null entries (nulls are position-preserving placeholders)
  const getKfaPhotoCount = (questionText: string): number =>
    (itemPhotos[questionText] ?? []).filter(Boolean).length;

  // ── Checklist state ────────────────────────────────────────────────────
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [customItems, setCustomItems]   = useState<Record<string, string[]>>({
    before: [], pit: [], ergo: [], process: [], after: [],
  });
  const [addInputs, setAddInputs] = useState<Record<string, string>>({
    before: '', pit: '', ergo: '', process: '', after: '',
  });

  // Collapsed state for During bands (key = band key, true = collapsed)
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set());

  // Checklist items fetched from GET /people/checklist-items, keyed by category
  const [fetchedQuestions, setFetchedQuestions] = useState<Record<ObsCategory, ObsFetchedItem[]>>({
    before: [], pit: [], ergo: [], process: [], after: [],
  });
  const [questionsLoading, setQuestionsLoading] = useState(false);

  const observationType = isRoutine ? 'Routine' : 'Full';

  // Restore custom checklist rows from resume payload, grouped by id prefix
  // (before/after/pit/ergo/process) so each row appears under the right stage/category.
  // A restored custom row is always checked — it only exists because the user added
  // and checked it before saving; there's no separate "unchecked custom row" state.
  useEffect(() => {
    const resumedCustomItems = mapResumedCustomItemsToCategories(session.customChecklist);
    const hasResumedCustomItems = OBS_CATEGORIES.some((cat) => resumedCustomItems[cat].length > 0);
    if (!hasResumedCustomItems) return;
    setCustomItems((prev) => {
      const next: Record<string, string[]> = { ...prev };
      OBS_CATEGORIES.forEach((cat) => {
        next[cat] = resumedCustomItems[cat];
      });
      return next;
    });
    setCheckedItems((prev) => {
      const next = new Set(prev);
      OBS_CATEGORIES.forEach((cat) => {
        resumedCustomItems[cat].forEach((text) => next.add(text));
      });
      return next;
    });
  }, [session.customChecklist]);

  useEffect(() => {
    let cancelled = false;
    const cats: ObsCategory[] = OBS_CATEGORIES;

    (async () => {
      setQuestionsLoading(true);
      try {
        const results = await Promise.all(
          cats.map((cat) =>
            fetchChecklistItems({
              observation_type: observationType,
              role,
              category: CHECKLIST_CATEGORY[cat],
              status: 1,
            }),
          ),
        );
        if (cancelled) return;
        const next = {} as Record<ObsCategory, ObsFetchedItem[]>;
        cats.forEach((cat, i) => {
          // Fall back to `observation` text when `question` is null
          next[cat] = (results[i] ?? [])
            .map((item) => ({ itemId: item.itemId, text: item.question ?? item.observation }))
            .filter((item): item is ObsFetchedItem => !!item.text);
        });
        setFetchedQuestions(next);

        // Map the resumed activity's checklist selections (itemId -> true) onto the
        // now-loaded catalog items so previously-checked rows show as checked.
        const resumedSelections = session.checklistSelections;
        if (resumedSelections) {
          const resumedChecked = new Set<string>();
          cats.forEach((cat) => {
            next[cat].forEach((item) => {
              if (resumedSelections[item.itemId]) resumedChecked.add(item.text);
            });
          });
          if (resumedChecked.size > 0) {
            setCheckedItems((prev) => new Set([...prev, ...resumedChecked]));
          }
        }

        // Map the resumed activity's saved images (checklistItemId -> image) onto the
        // matching catalog item's question text so the camera badge/count shows on resume.
        const resumedImages = session.checklistImages;
        if (resumedImages && resumedImages.length > 0) {
          const itemIdToText: Record<string, string> = {};
          cats.forEach((cat) => {
            next[cat].forEach((item) => { itemIdToText[String(item.itemId)] = item.text; });
          });

          // Include custom checklist ids from resume payload (e.g., before_2) so
          // their images can be mapped back to the custom row text as well.
          (session.customChecklist ?? []).forEach((item) => {
            if (item?.id && item?.checklistItem) {
              itemIdToText[String(item.id)] = item.checklistItem;
            }
          });

          const photosByText: Record<string, KfaImage[]> = {};
          resumedImages.forEach((img) => {
            const text = itemIdToText[String(img.checklistItemId)];
            if (!text) return;
            const photo: KfaImage = {
              uri: img.imageBase64,
              description: img.description,
              imageUrl: img.imageBase64,
              imageBase64: img.imageBase64,
            };
            // Place at its saved slot (imageId "1"/"2") rather than push order,
            // so an image uploaded in the 2nd slot resumes into the 2nd slot.
            const slotIndex = Math.max(0, (parseInt(String(img.imageId), 10) || 1) - 1);
            const arr = (photosByText[text] ??= []);
            arr[slotIndex] = photo;
          });
          if (Object.keys(photosByText).length > 0) {
            setItemPhotos((prev) => ({ ...prev, ...photosByText }));
          }
        }
      } catch (err) {
        console.warn('Failed to fetch observation checklist items:', err);
      } finally {
        if (!cancelled) setQuestionsLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observationType, role]);

  // Derived: all visible questions per category (API + custom), text-only for rendering
  const allQuestions = useMemo(() => {
    const cats: ObsCategory[] = ['before', 'pit', 'ergo', 'process', 'after'];
    const result: Record<string, string[]> = {};
    cats.forEach((cat) => {
      result[cat] = [...(fetchedQuestions[cat] ?? []).map((i) => i.text), ...(customItems[cat] ?? [])];
    });
    return result;
  }, [fetchedQuestions, customItems]);

  const toggleItem = (text: string, checked: boolean) => {
    setCheckedItems((prev) => {
      const next = new Set(prev);
      checked ? next.add(text) : next.delete(text);
      return next;
    });
    // Deselecting a KFA removes any images attached to it
    if (!checked) {
      setItemPhotos((prev) => {
        if (!(text in prev)) return prev;
        const next = { ...prev };
        delete next[text];
        return next;
      });
    }
  };

  const addCustomItem = (cat: string) => {
    const val = (addInputs[cat] ?? '').trim();
    if (!val) return;
    setCustomItems((prev) => ({ ...prev, [cat]: [...(prev[cat] ?? []), val] }));
    setAddInputs((prev) => ({ ...prev, [cat]: '' }));
  };

  const setAddInput = (cat: string, val: string) => {
    setAddInputs((prev) => ({ ...prev, [cat]: val }));
  };

  const toggleBand = (key: string) => {
    setCollapsedBands((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const checkedCount = (cat: string) =>
    allQuestions[cat]?.filter((t) => checkedItems.has(t)).length ?? 0;

  // ── Coaching tab state (mirrors PulseCheck) ────────────────────────────
  const [openItems, setOpenItems]     = useState<CoachingHistoryItem[]>(() => buildMockOpenItems(session.employeeId));
  const [historyItems, setHistoryItems] = useState<CoachingHistoryItem[]>(() => buildMockHistoryItems(session.employeeId));
  const [feedback, setFeedback]       = useState<BalancedFeedbackData>(() => ({
    good: session.resumedFeedback?.recognize ?? session.resumedFeedback?.well ?? '',
    improve: session.resumedFeedback?.coach ?? session.resumedFeedback?.improve ?? '',
    keep: session.resumedFeedback?.sustain ?? session.resumedFeedback?.keep ?? '',
    support: '',
  }));
  const [actionDraft, setActionDraft] = useState<NewActionDraft>({ type: '', focus: '', otherFocus: '', opportunity: '', plan: '' });
  const [sigData, setSigData]         = useState<SignatureData>(() =>
    mapResumedSignatures(
      session.resumedSignatures ?? session.signatures,
      session.employeeName,
      session.managerName,
    ),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSignatureUploading, setIsSignatureUploading] = useState(false);

  // Swaps the mock coaching rows for the employee's real coachingItems once the profile
  // fetch (dispatched alongside the resume call) lands. An empty array is a valid "no open
  // items" result and must still replace the mock rows, so only bail when the profile
  // hasn't loaded yet (coachingItems is undefined).
  useEffect(() => {
    const items = profileData?.coachingItems;
    if (!Array.isArray(items)) return;
    const { openItems: apiOpen, historyItems: apiHistory } = mapCoachingHistoryItems(items, session.employeeId);
    const rawResult = typeof session.resumedCoachingResult === 'object' && session.resumedCoachingResult !== null
      ? (session.resumedCoachingResult as any).followup_result ?? (session.resumedCoachingResult as any).status
      : session.resumedCoachingResult;
    const resumedResult = fromCoachingResultEnum(rawResult);
    const resumedSupervisorFeedback = typeof session.resumedCoachingResult === 'object' && session.resumedCoachingResult !== null
      ? (session.resumedCoachingResult as any).supervisorFeedback ?? session.resumedSupervisorFeedback
      : session.resumedSupervisorFeedback;
    if (apiOpen[0] && resumedResult) {
      apiOpen[0] = {
        ...apiOpen[0],
        followUpResult: resumedResult,
        followUpFeedback: resumedSupervisorFeedback ?? '',
      };
    }
    setOpenItems(apiOpen);
    setHistoryItems(apiHistory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);

  const handleFollowUpResult = (id: string, result: FollowUpResult) => {
    setOpenItems((prev) => prev.map((item) => item.id === id ? { ...item, followUpResult: result, followUpFeedback: '' } : item));
  };
  const handleFollowUpFeedback = (id: string, text: string) => {
    setOpenItems((prev) => prev.map((item) => item.id === id ? { ...item, followUpFeedback: text } : item));
  };

  const allResultsRecorded = openItems.length === 0 || openItems.every((i) => !!i.followUpResult);
  const allFeedbackComplete = openItems.every((i) => {
    const needs = i.followUpResult === 'Shows Improvement' || i.followUpResult === 'No Improvement Shown';
    return !needs || i.followUpFeedback.trim().length >= 10;
  });
  const allItemsComplete = openItems.length === 0 || openItems.every((i) => i.followUpResult === 'Complete');
  const newActionGated = !allItemsComplete;
  const gateMessage = newActionGated
    ? 'Select Shows Improvement, No Improvement Shown, or Complete on every open item before adding a new action.'
    : undefined;

  const newActionPlanContext = useMemo(() => ({
    employeeId: session.employeeId,
    employeeName: session.employeeName,
    activityId: session.activityId,
    userInfo,
    // profileData.employee.manager (the live /people/employees/{id} record) is the
    // authoritative source — session.managerName can be stale/wrong on resume.
    ownerName: profileData?.employee?.manager ?? session.managerName,
    ownerEmail: profileData?.employee?.managerEmail,
    ownerGpid: profileData?.employee?.managerGpid,
    locationName: selectedLocation?.name ?? selectedLocation?.displayText,
    locationId: selectedLocation?.id,
    directoryUsers: [...fieldUsers, ...coeUsers],
    employeeRoster,
  }), [session, userInfo, selectedLocation, fieldUsers, coeUsers, employeeRoster, profileData]);

  const validationError = useMemo<string | null>(() => {
    if (step < 3) return null; // only validate on Coaching tab
    if (!allResultsRecorded) return 'Record a follow up result for every open coaching item before completing.';
    if (!allFeedbackComplete) return 'Supervisor Feedback must contain at least 10 characters when Shows Improvement or No Improvement Shown is selected.';
    if (actionDraft.type && !allItemsComplete) return 'A new action item can only be created when every open item is marked Complete.';
    const newActionErr = validateNewActionPlan(actionDraft, newActionPlanContext);
    if (newActionErr) return newActionErr;
    const hasEmployeeSig = !!(sigData.employeeSignature?.imageData || sigData.employeeSignature?.imageUrl);
    const hasManagerSig = !!(sigData.managerSignature?.imageData || sigData.managerSignature?.imageUrl);
    if (!hasEmployeeSig || !hasManagerSig)
      return 'Employee and manager signatures are required before completing the Observation.';
    if (isSignatureUploading)
      return 'Waiting for signatures to finish uploading…';
    return null;
  }, [step, openItems, actionDraft, sigData, isSignatureUploading, newActionPlanContext]);

  const canComplete = step === 3 && validationError === null;

  // Builds { checklistSelections, images } for the given categories, keyed by backend itemId.
  // Only checked items are included in checklistSelections (unchecked/untouched are omitted).
  // Custom (user-added) items have no itemId so they're skipped from the catalog payload.
  const buildChecklistPayload = (cats: ObsCategory[]) => {
    const checklistSelections: Record<string, boolean> = {};
    const images: ActivityImagePayload[] = [];
    cats.forEach((cat) => {
      (fetchedQuestions[cat] ?? []).forEach((item) => {
        if (checkedItems.has(item.text)) checklistSelections[item.itemId] = true;
        // Compute imageId from the slot position before filtering out empty
        // slots, so an image in the 2nd upload component always sends imageId "2".
        (itemPhotos[item.text] ?? [])
          .map((photo, idx) => ({ photo, idx }))
          .filter((entry): entry is { photo: KfaImage; idx: number } => !!entry.photo)
          .forEach(({ photo, idx }) => {
            images.push({
              checklistItemId: item.itemId,
              imageId: String(idx + 1),
              imageBase64: photo.imageUrl ?? photo.fileName ?? '',
              description: photo.description,
            });
          });
      });

      // Custom checklist rows reuse the same image payload shape as catalog rows,
      // with checklistItemId tied to the generated custom checklist id.
      (customItems[cat] ?? []).forEach((itemText, idx) => {
        const customItemId = `${cat}_${idx + 1}`;
        (itemPhotos[itemText] ?? [])
          .map((photo, imageIdx) => ({ photo, imageIdx }))
          .filter((entry): entry is { photo: KfaImage; imageIdx: number } => !!entry.photo)
          .forEach(({ photo, imageIdx }) => {
            images.push({
              checklistItemId: customItemId,
              imageId: String(imageIdx + 1),
              imageBase64: photo.imageUrl ?? photo.fileName ?? '',
              description: photo.description,
            });
          });
      });
    });
    return { checklistSelections, images };
  };

  const buildCustomChecklistPayload = (cats: ObsCategory[]): CustomChecklistItemPayload[] => {
    const customChecklist: CustomChecklistItemPayload[] = [];
    cats.forEach((cat) => {
      (customItems[cat] ?? []).forEach((itemText, idx) => {
        customChecklist.push({
          id: `${cat}_${idx + 1}`,
          checklistItem: itemText,
        });
      });
    });
    return customChecklist;
  };

  const observationTypeLabel = isRoutine ? 'Routine' : 'Full';

  const handleSaveDraft = async () => {
    if (session.activityId == null) return;
    // Never call the API while the New Action form is incomplete or unresolved.
    if (validateNewActionPlan(actionDraft, newActionPlanContext)) return;
    // Save the current stage plus every stage already reviewed before it, so KFAs
    // selected on earlier stages aren't dropped when saving from a later one.
    const stageName = step < 3 ? STEP_STAGE[step as 0 | 1 | 2] : undefined;
    const cats = stageName
      ? STEP_STAGE.slice(0, step + 1).flatMap((s) => STAGE_CATEGORIES[s])
      : (['before', 'pit', 'ergo', 'process', 'after'] as ObsCategory[]);
    const { checklistSelections, images } = buildChecklistPayload(cats);
    const customChecklist = buildCustomChecklistPayload(cats);
    const primaryOpenItem = openItems[0];
    const payload: Record<string, any> = {
      activityType: 'OBSERVATION',
      observationType: observationTypeLabel,
      checklistSelections,
      customChecklist,
      images,
      balance_feedback: {
        recognize: feedback.good,
        coach: feedback.improve,
        sustain: feedback.keep,
      },
      coachingResult: buildCoachingResultPayload(primaryOpenItem),
      // Sent on every save, not just complete, so an in-progress New Action draft isn't
      // silently dropped — undefined when blank or when assignee/owner can't be resolved.
      newAction: buildNewActionPlanPayload(actionDraft, newActionPlanContext),
    };
    if (stageName) payload.stage = stageName;
    const { employeeSignature, managerSignature } = toSignaturePayload(sigData);
    payload.employeeSignature = employeeSignature;
    payload.managerSignature = managerSignature;
    try {
      await saveActivityDraft(session.activityId, withCreatedBy(payload, userInfo));
    } catch (err) {
      console.warn('Failed to save observation draft:', err);
    }
  };

  const handleComplete = async (onDone: () => void) => {
    if (!canComplete || session.activityId == null) return;
    setIsSubmitting(true);
    try {
      const allCats: ObsCategory[] = ['before', 'pit', 'ergo', 'process', 'after'];
      const { checklistSelections, images } = buildChecklistPayload(allCats);
      const customChecklist = buildCustomChecklistPayload(allCats);
      const primaryOpenItem = openItems[0];
      const { employeeSignature, managerSignature } = toSignaturePayload(sigData);
      await completeActivity(session.activityId, withCreatedBy({
        activityType: 'OBSERVATION',
        observationType: observationTypeLabel,
        balance_feedback: {
          recognize: feedback.good,
          coach: feedback.improve,
          sustain: feedback.keep,
        },
        checklistSelections,
        customChecklist,
        images,
        coachingResult: buildCoachingResultPayload(primaryOpenItem),
        // Full Action Plan creation fields (title/source/type/priority/owner/assignee/dates) —
        // undefined when the New Action form was left blank, or when assignee/owner identity
        // fields can't be resolved (buildNewActionPlanPayload guards via validateNewActionAssignment).
        newAction: buildNewActionPlanPayload(actionDraft, newActionPlanContext),
        employeeSignature,
        managerSignature,
      }, userInfo));
      onDone();
    } catch (err) {
      console.warn('Failed to complete observation:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    // metrics
    metricRows,
    periodMetricRows,
    // step navigation
    step, reached, goNext, goBack, goToStep,
    // checklist
    allQuestions, checkedItems, addInputs, questionsLoading,
    collapsedBands, toggleItem, addCustomItem, setAddInput, toggleBand, checkedCount,
    // per-KFA photos
    itemPhotos, setKfaPhotos, getKfaPhotos, getKfaPhotoCount,
    // coaching
    feedback, setFeedback,
    openItems, historyItems,
    actionDraft, setActionDraft,
    sigData, setSigData,
    isSignatureUploading, setIsSignatureUploading,
    handleFollowUpResult, handleFollowUpFeedback,
    newActionGated, gateMessage,
    // submit
    validationError, canComplete, isSubmitting,
    handleSaveDraft, handleComplete,
  };
}
